/* eslint-disable react/prop-types */
import React, { useState, useMemo } from "react";
import { OverlayTrigger, Tooltip, Col, Row } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useForm, FormProvider } from "react-hook-form";

import { deleteReport } from "api";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import DataTable from "components/organisms/DataTable/DataTable";
import usePermissions from "hooks/usePermissions";
import { ReactComponent as DownloadIcon } from "assets/images/icons/Import.svg";
import { ReactComponent as RemoveIcon } from "assets/images/icons/CircleCross.svg";
import ScheduledReports from "pages/common/Analytics/components/Reports/ScheduledReports/ScheduledReports";
import { useGetReport } from "./Report/useGetReport";

function Reports() {
  const intl = useIntl();
  const navigate = useNavigate();
  const methods = useForm({ shouldFocusError: true });
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");

  const [showDialog, setShowDialog] = useState(false);
  const [reportId, setReportId] = useState("");

  const openRemoveDialog = (id) => {
    setReportId(id);
    setShowDialog(true);
  };

  const handleReportSubmit = () => {
    if (reportId) {
      deleteReport(reportId)
        .then((res) => {
          if (res) {
            refetch(true);
            setShowDialog(false);
            toast(
              intl.formatMessage({
                id: "report.successfullyDeleted",
                defaultMessage: "Report successfully deleted.",
              }),
              { type: "success" },
            );
          }
        })
        .catch((error) => {
          setShowDialog(false);
          errorToast(
            error,
            intl.formatMessage({
              id: "report.couldNotDeleteError",
              defaultMessage: "Report could not be deleted. Please try again later.",
            }),
          );
        });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: "report.created", defaultMessage: "Created" }),
        accessor: "created",
        sortType: "basic",
        sortable: true,
        Cell: ({ value }) => (
          <div>
            <p className="mb-0 font-b9 mr-1">{formatDate(value, DateTimeFormat.dateFormat)}</p>
            <p className="mb-0 font-b10">{formatDate(value, DateTimeFormat.timeFormat)} UTC</p>
          </div>
        ),
      },
      {
        Header: intl.formatMessage({ id: "report.type", defaultMessage: "Type" }),
        accessor: "type",
        sortType: "basic",
        sortable: true,
        Cell: ({ value, row }) => (
          <div>
            <p className="mb-0">{value}</p>
            <p className="mb-0 text-secondary">
              {row.original.number_of_rows} <FormattedMessage id="general.records" defaultMessage="records" />
            </p>
          </div>
        ),
      },
      {
        Header: intl.formatMessage({ id: "report.dateRange", defaultMessage: "Date Range" }),
        accessor: "col4",
        sortable: true,
        id: "start_date",
        Cell: ({ row }) => {
          const value = [row.original.start_date, row.original.end_date];
          return (
            <p className="mb-0 d-flex align-items-center">
              {`${moment(value[0]).format("DD MMM YYYY")} ${" - "} ${moment(value[1]).format("DD MMM YYYY")}`}
            </p>
          );
        },
      },
      {
        Header: intl.formatMessage({ id: "general.freightMode", defaultMessage: "Freight Mode" }),
        accessor: "freight_modes",
        sortable: false,
        id: "freight_mode",
        Cell: ({ value }) => (
          <div>
            {value?.length > 0
              ? value.map(({ name, type }, index) => {
                  const Icon = getFreightModeIcon(name, type, true);
                  return (
                    <span className="mr-2" key={index}>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="freight-mode">
                            <span className="font-weight-normal">{name}</span>
                          </Tooltip>
                        }
                      >
                        <Icon />
                      </OverlayTrigger>
                    </span>
                  );
                })
              : "-"}
          </div>
        ),
      },
      ...(user_type === "shipper"
        ? [
            {
              Header: intl.formatMessage({ id: "report.paymentLocation", defaultMessage: "Payment Location" }),
              accessor: "payment_locations",
              sortable: false,
              id: "payment_location",
              Cell: ({ value }) => (
                <p className="mb-0 d-flex align-items-center">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        <span className="font-weight-normal">{value.map((label) => label.name).join(", ")}</span>
                      </Tooltip>
                    }
                  >
                    <span className="mt-2">{value.map((label) => label.name)[0]}</span>
                  </OverlayTrigger>
                </p>
              ),
            },
          ]
        : []),
      ...(user_type === "provider"
        ? [
            {
              Header: intl.formatMessage({ id: "settings.side-menu.teams", defaultMessage: "Teams" }),
              accessor: "teams",
              sortable: false,
              id: "teams",
              Cell: ({ value }) => (
                <p className="mb-0 d-flex align-items-center">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        <span className="font-weight-normal">{value.map((label) => label.team_name).join(", ")}</span>
                      </Tooltip>
                    }
                  >
                    <span className="mt-2">
                      {value
                        .slice(0, 2)
                        .map((label) => label.team_name)
                        .join(", ")}
                      {value.length > 2 ? `, +${value.length - 2}` : ""}
                    </span>
                  </OverlayTrigger>
                </p>
              ),
            },
          ]
        : []),
      {
        Header: intl.formatMessage({ id: "report.action", defaultMessage: "Action" }),
        accessor: "col7",
        sortable: false,
        id: "action",
        Cell: ({ row }) => {
          const [link, numberOfRows, status, id] = [
            row.original.link,
            row.original.number_of_rows,
            row.original.status,
            row.original.id,
          ];

          if (status === "generated" && numberOfRows !== 0) {
            return (
              <p className="mb-0 d-flex align-items-center">
                <a testid={testIds.ANALYTICS_REPORT_DOWNLOAD_BUTTON} className="mr-2" href={link} download>
                  <DownloadIcon width={20} />
                </a>
                <Button
                  variant="link"
                  className="pl-0 pr-0 text-danger ml-2"
                  data-testid={testIds.ANALYTICS_REPORT_DELETE_BUTTON}
                  onClick={() => openRemoveDialog(id)}
                >
                  <RemoveIcon width={30} height={20} />
                </Button>
              </p>
            );
          }
          if (status === "processing") {
            return (
              <p className="mb-0 d-flex align-items-center">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <span className="font-weight-normal">
                        <FormattedMessage
                          id="report.generateReportMintue"
                          defaultMessage=" This may take up to a few minutes."
                        />
                      </span>
                    </Tooltip>
                  }
                >
                  <span className="mt-2 font-b3">
                    <FormattedMessage id="report.generating" defaultMessage="Generating report" />
                  </span>
                </OverlayTrigger>
              </p>
            );
          }
          return (
            <p className="mb-0 d-flex align-items-center ">
              <span className="mr-2">
                <FormattedMessage id="report.0Records" defaultMessage="0 Records" />
              </span>
              <span>|</span>
              <Button
                variant="link"
                className="pl-0 pr-0 text-danger ml-2"
                data-testid={testIds.ANALYTICS_REPORT_DELETE_BUTTON}
                onClick={() => openRemoveDialog(id)}
              >
                <RemoveIcon width={30} height={20} />
              </Button>
            </p>
          );
        },
      },
    ],
    [],
  );

  const { instance, loading, totalData, refetch } = useTableQueryRequest(useGetReport, columns);

  return (
    <>
      <Dialog
        title={intl.formatMessage({
          id: "report.cancelReport",
          defaultMessage: "Cancel Report",
        })}
        size="md"
        onHide={() => setShowDialog(false)}
        show={showDialog}
      >
        <FormProvider {...methods}>
          <Row className="mb-3 mt-3">
            <Col lg={12} className="text-center">
              <span className="font-b1">
                <FormattedMessage id="report.alertMessage" defaultMessage="Are you sure you want to delete this report?" />
              </span>
            </Col>
            <Col lg={12} className="text-center">
              <span className="font-b1">
                <FormattedMessage id="report.alertMessageUndone" defaultMessage=" This cannot be undone." />
              </span>
            </Col>
          </Row>
          <Row className="mt-5">
            <Col lg={12} className="d-flex flex-row justify-content-between">
              <Button
                className="btn-grey"
                label={intl.formatMessage({ id: "report.discard", defaultMessage: "Discard" })}
                onClick={() => {
                  setShowDialog(false);
                }}
              />
              <Button
                variant="success"
                label={intl.formatMessage({ id: "report.submitDialog", defaultMessage: "Yes, I'm sure" })}
                onClick={handleReportSubmit}
                data-testid={testIds.ANALYTICS_REPORT_DELETE_CONFIRM_BUTTON}
              />
            </Col>
          </Row>
        </FormProvider>
      </Dialog>
      <Row className="mt-3">
        <Col lg={9} md={7} xs={12}>
          <DataTable
            headerLeft={
              <h5 className="mb-0 mt-3">
                <FormattedMessage id="report.generatedReports" defaultMessage="Generated Reports" />
              </h5>
            }
            headerRight={
              user_type === "shipper" ? (
                <Button
                  to="/analytics?filter=new report"
                  className="rounded-pill mt-3"
                  id="New-report"
                  data-testid={testIds.ANALYTICS_CREATE_REPORT_BUTTON}
                  onClick={() => {
                    navigate("/analytics?filter=new report");
                  }}
                >
                  <FormattedMessage id="report.newReport" defaultMessage="New Report" />
                </Button>
              ) : null
            }
            tableInstance={instance}
            totalData={totalData}
            isLoading={loading}
          />
        </Col>
        <Col lg={3} md={5} xs={12}>
          <ScheduledReports />
        </Col>
      </Row>
    </>
  );
}

Reports.propTypes = {
  value: PropTypes.instanceOf(Array),
  showTitle: PropTypes.bool,
};

Reports.defaultProps = {
  value: [],
  showTitle: false,
};

export default Reports;
